import { EntityType } from '@frontend/common';
import { Action, PayloadAction, ThunkDispatch, createSlice } from '@reduxjs/toolkit';

import { SELF_CREATE_MQTT_SUPPRESS_MS } from './table-auto-refresh';

export interface EntityCreateSignalEntry {
    count: number;
    dirtyAt: number;
}

export type EntityCreateSignalState = Partial<Record<EntityType, EntityCreateSignalEntry>>;

const initialState: EntityCreateSignalState = {};

/** Synchronous stamp so MQTT handlers can ignore create echoes before React re-renders. */
const lastLocalCreateAtByType: Partial<Record<EntityType, number>> = {};

export const entityCreateSignalSlice = createSlice({
    name: 'entityCreateSignals',
    initialState,
    reducers: {
        notifyEntityCreated(state, action: PayloadAction<{ entityType: EntityType; dirtyAt?: number }>) {
            const { entityType } = action.payload;
            const previous = state[entityType];
            state[entityType] = {
                count: (previous?.count ?? 0) + 1,
                dirtyAt: action.payload.dirtyAt ?? Date.now()
            };
        }
    }
});

export const entityCreateSignalStore = { entityCreateSignals: entityCreateSignalSlice.reducer };

export const { notifyEntityCreated: notifyEntityCreatedAction } = entityCreateSignalSlice.actions;

/** One-liner for create success handlers that already have a dispatch. */
export function notifyEntityCreated(dispatch: ThunkDispatch<any, any, Action>, entityType: EntityType): void {
    lastLocalCreateAtByType[entityType] = Date.now();
    dispatch(notifyEntityCreatedAction({ entityType }));
}

export function wasEntityRecentlyCreatedLocally(entityType: EntityType, withinMs: number = SELF_CREATE_MQTT_SUPPRESS_MS, now: number = Date.now()): boolean {
    const at = lastLocalCreateAtByType[entityType];
    return at != null && now - at < withinMs;
}

/** Test helper — do not use in app code. */
export function resetLocalCreateTimestampsForTests(): void {
    for (const key of Object.keys(lastLocalCreateAtByType) as EntityType[]) {
        delete lastLocalCreateAtByType[key];
    }
}
